import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Send, Loader2, Bot, ChevronDown, CheckCircle2, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { api } from "../lib/api";

export const SupportTelegramSettings = () => {
  const [cfg, setCfg] = useState({ enabled: false, ai_enabled: true, chat_id: "", bot_set: false, bot_username: "", replies_enabled: false });
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const [chats, setChats] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try { setCfg(await api.getSupportTelegram()); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (body, label) => {
    setBusy(label);
    try {
      const d = await api.saveSupportTelegram(body);
      setCfg(d);
      return d;
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); throw e; }
    finally { setBusy(""); }
  };

  const saveToken = async () => {
    if (!token.trim()) { toast.warning("ألصق توكن البوت أولاً"); return; }
    await patch({ bot_token: token.trim() }, "token");
    setToken("");
    toast.success("تم حفظ التوكن");
  };

  const discover = async () => {
    setBusy("discover");
    try {
      const d = await api.discoverSupportTelegram();
      setChats(d.chats || []);
      toast.success("تم جلب المحادثات — اختر محادثتك");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الجلب"); }
    finally { setBusy(""); }
  };

  const pickChat = async (id) => {
    await patch({ chat_id: id }, "chat");
    toast.success("تم ربط المحادثة");
  };

  const sendTest = async () => {
    setBusy("test");
    try { await api.testSupportTelegram(); toast.success("أُرسلت رسالة تجربة إلى تيليجرام ✅"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); }
    finally { setBusy(""); }
  };

  const toggleReplies = async (v) => {
    setBusy("replies");
    try {
      const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const d = await api.toggleSupportTelegramReplies(v, base);
      setCfg((c) => ({ ...c, replies_enabled: d.replies_enabled }));
      toast.success(v ? "تم تفعيل الرد المباشر عبر تيليجرام ✅" : "تم إيقاف الرد عبر تيليجرام");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التغيير"); }
    finally { setBusy(""); }
  };

  const ready = cfg.bot_set && !!cfg.chat_id;

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/60 overflow-hidden" data-testid="support-telegram-settings">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-right hover:bg-sky-100/50">
        <span className="flex items-center gap-2 text-sm font-bold text-sky-900">
          <Bot className="h-5 w-5 text-sky-600" /> الوكيل الذكي — تنبيهات الدعم على تيليجرام
          <span className={`text-[10px] rounded-full px-2 py-0.5 ${cfg.enabled && ready ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
            {cfg.enabled && ready ? "مُفعّل" : "غير مُفعّل"}
          </span>
        </span>
        <ChevronDown className={`h-4 w-4 text-sky-500 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-sky-100 p-3 space-y-4 bg-white/70">
          <ol className="text-[11px] text-slate-500 leading-relaxed list-decimal pr-4 space-y-0.5">
            <li>افتح تيليجرام وابحث عن <b>@BotFather</b> وأرسل <code>/newbot</code> لإنشاء بوت والحصول على التوكن.</li>
            <li>ألصق التوكن بالأسفل واحفظه.</li>
            <li>أرسل رسالة <code>/start</code> لبوتك من جوالك، ثم اضغط «اكتشف المحادثات» واختر محادثتك.</li>
            <li>فعّل التنبيهات وأرسل رسالة تجربة.</li>
          </ol>

          <div className="space-y-1.5">
            <Label className="text-xs">توكن البوت (Bot Token)
              {cfg.bot_set && <span className="text-emerald-600 mr-1 inline-flex items-center gap-0.5"><CheckCircle2 className="h-3 w-3" /> محفوظ</span>}
            </Label>
            <div className="flex gap-2">
              <Input data-testid="tg-token-input" dir="ltr" type="password" value={token} onChange={(e) => setToken(e.target.value)}
                placeholder={cfg.bot_set ? "••••••••  (أدخل توكن جديد للتبديل)" : "123456:ABC-DEF..."} className="flex-1 font-mono-nums text-xs" />
              <Button data-testid="tg-save-token" onClick={saveToken} disabled={busy === "token"} size="sm" className="bg-sky-600 hover:bg-sky-700 shrink-0">
                {busy === "token" ? <Loader2 className="h-4 w-4 animate-spin" /> : "حفظ"}
              </Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">محادثة الاستقبال</Label>
            {cfg.bot_username && (
              <a href={`https://t.me/${cfg.bot_username}`} target="_blank" rel="noreferrer"
                data-testid="tg-open-bot"
                className="flex items-center gap-1.5 text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-lg px-2.5 py-1.5 w-fit hover:bg-sky-100">
                <ExternalLink className="h-3.5 w-3.5" /> افتح بوتك @{cfg.bot_username} وأرسل <code className="font-mono-nums">/start</code>
              </a>
            )}
            <div className="flex gap-2 items-center">
              <Button data-testid="tg-discover" onClick={discover} disabled={busy === "discover" || !cfg.bot_set} size="sm" variant="outline" className="shrink-0 gap-1.5">
                {busy === "discover" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} اكتشف المحادثات
              </Button>
              <Select value={cfg.chat_id || ""} onValueChange={pickChat}>
                <SelectTrigger data-testid="tg-chat-select" className="flex-1 bg-white text-xs"><SelectValue placeholder="اختر محادثتك" /></SelectTrigger>
                <SelectContent>
                  {chats.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name} <span dir="ltr" className="text-slate-400">({c.id})</span></SelectItem>))}
                  {cfg.chat_id && !chats.some((c) => c.id === cfg.chat_id) && (<SelectItem value={cfg.chat_id}>المحادثة المربوطة ({cfg.chat_id})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div>
              <p className="text-sm font-semibold text-slate-800">تفعيل التنبيهات</p>
              <p className="text-[11px] text-slate-400">يصلك إشعار عند أي تذكرة جديدة أو رد من عميل</p>
            </div>
            <Switch data-testid="tg-enabled-switch" checked={cfg.enabled} disabled={!ready}
              onCheckedChange={(v) => patch({ enabled: v }, "enabled")} />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div>
              <p className="text-sm font-semibold text-slate-800">الملخص الذكي + اقتراح الرد</p>
              <p className="text-[11px] text-slate-400">يضيف ملخصاً وتصنيف أولوية ورداً مقترحاً داخل الإشعار</p>
            </div>
            <Switch data-testid="tg-ai-switch" checked={cfg.ai_enabled}
              onCheckedChange={(v) => patch({ ai_enabled: v }, "ai")} />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div>
              <p className="text-sm font-semibold text-slate-800">الرد المباشر على العميل من تيليجرام</p>
              <p className="text-[11px] text-slate-400">اسحب أي إشعار واختر Reply واكتب ردك — سيصل العميل داخل التذكرة</p>
            </div>
            <Switch data-testid="tg-replies-switch" checked={cfg.replies_enabled} disabled={!ready || busy === "replies"}
              onCheckedChange={toggleReplies} />
          </div>

          <Button data-testid="tg-test" onClick={sendTest} disabled={busy === "test" || !ready} variant="outline" className="w-full gap-1.5">
            {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} إرسال رسالة تجربة
          </Button>
        </div>
      )}
    </div>
  );
};

export default SupportTelegramSettings;
